import { useState, useEffect } from 'react';
import { ArrowRight, Calendar } from 'lucide-react';

const DEFAULT_POSTS = [
  
];

function fetchWpPosts(siteUrl) {
  return fetch(`${siteUrl}/wp-json/wp/v2/posts?per_page=4&_embed`)
    .then(r => r.ok ? r.json() : [])
    .catch(() => []);
}

export default function BlogSection({
  title    = 'Fun Tips & Parenting Reads',
  subtitle = 'Helpful guides, toy reviews, and parenting tips for you! 🌟',
  badge    = '📝 Our Blog',
  blogUrl  = '#',
}) {
  const [posts, setPosts] = useState(DEFAULT_POSTS);

  // Try fetching real WP posts; fall back to defaults
  useEffect(() => {
    const siteUrl = (window.themeData?.siteUrl ?? '').replace(/\/$/, '');
    if (!siteUrl) return;
    fetchWpPosts(siteUrl).then(data => {
      if (!Array.isArray(data) || data.length === 0) return;
      const accentBorders = ['border-primary/10', 'border-accent2/10', 'border-accent3/10', 'border-secondary/20'];
      const catColors     = ['bg-accent1/15 text-accent1', 'bg-accent2/15 text-accent2', 'bg-accent3/15 text-accent3', 'bg-secondary/20 text-yellow-600'];
      const linkColors    = ['text-primary hover:text-primaryHover', 'text-accent2 hover:text-blue-600', 'text-accent3 hover:text-green-600', 'text-yellow-600 hover:text-yellow-700'];
      setPosts(data.map((p, i) => ({
        id:          p.id,
        title:       p.title?.rendered ?? 'Blog Post',
        excerpt:     p.excerpt?.rendered?.replace(/<[^>]+>/g, '').slice(0, 160) + '…' ?? '',
        date:        new Date(p.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        image:       p._embedded?.['wp:featuredmedia']?.[0]?.source_url ?? null,
        link:        p.link ?? '#',
        category:    p._embedded?.['wp:term']?.[0]?.[0]?.name ?? 'Blog',
        catColor:    catColors[i % catColors.length],
        linkColor:   linkColors[i % linkColors.length],
        borderColor: accentBorders[i % accentBorders.length],
      })));
    });
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-block bg-primary/10 text-primary font-black text-sm px-5 py-2 rounded-full mb-4">{badge}</span>
          <h2 className="text-4xl md:text-5xl font-black text-textDark mb-4">{title}</h2>
          {subtitle && <p className="text-xl text-textLight">{subtitle}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {posts.map(post => (
            <a
              key={post.id}
              href={post.link ?? '#'}
              className={`group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border-2 ${post.borderColor} flex flex-col sm:flex-row`}
            >
              {/* Image */}
              <div className="sm:w-48 w-full h-48 sm:h-auto flex-shrink-0 overflow-hidden bg-gray-100">
                {post.image
                  ? <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  : <div className="w-full h-full flex items-center justify-center text-6xl bg-gradient-to-br from-primary/10 to-accent1/10 group-hover:scale-105 transition-transform duration-500">
                      {post.category?.startsWith('🧩') ? '🧩' : post.category?.startsWith('🚗') ? '🚗' : post.category?.startsWith('📚') ? '📚' : '🎨'}
                    </div>
                }
              </div>

              {/* Content */}
              <div className="flex flex-col justify-between p-6 flex-1">
                <div>
                  <span className={`inline-block ${post.catColor} text-xs font-black px-3 py-1 rounded-full mb-3`}>{post.category}</span>
                  <h3 className={`font-black text-textDark text-lg leading-snug mb-2 transition-colors ${post.linkColor.replace('hover:', 'group-hover:')}`}>
                    {post.title}
                  </h3>
                  <p className="text-textLight text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-textLight flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />{post.date}
                  </span>
                  <span className={`inline-flex items-center gap-2 font-black text-sm transition-all group-hover:gap-3 ${post.linkColor}`}>
                    Read More <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {blogUrl && blogUrl !== '#' && (
          <div className="mt-10 text-center">
            <a href={blogUrl} className="inline-flex items-center gap-2 bg-primary hover:bg-primaryHover text-white font-black px-8 py-4 rounded-full shadow-soft transition-colors">
              View All Posts <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
